import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { LanguageCode, Translation } from '../../Models/Models';
import { SiteContext } from '../../Contexts';
import { Utils } from '../../Services/Utils';
import { quillModules } from '../../Models/quillModules';
import { TextService } from '../../Services/TextService';
import { EntityColors } from '../../Themes/EntityColors';
import DiscardButton from '../Buttons/DiscardButton';
import SaveButton from '../Buttons/SaveButton';
import DeleteButton from '../Buttons/DeleteButton';
import EditButton from '../Buttons/EditButton';
import FormShell from '../Form/FormShell';
import { useIsDirty } from '../../Hooks/useIsDirty';
import { FlagImage, AddLanguageTile, LanguagePickerModal, EnableLanguagesHint } from '../Language/LanguagePicker';
import TranslationLanguageBadges from '../Language/TranslationLanguageBadges';
import { useSiteLanguages } from '../../Hooks/useSiteLanguages';
import { languageName } from '../../Services/languageNames';

const TextEditComponent: React.FC = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Úprava textu');
    }, []);

    const { getText, updateText } = TextService();
    const { currentSite }: any = useContext(SiteContext);
    const [translations, setTranslations] = useState<Translation[]>([]);
    const [pageId, setPageId] = useState<number | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [showAddLanguageModal, setShowAddLanguageModal] = useState(false);
    const [showChangeLanguageModal, setShowChangeLanguageModal] = useState(false);
    const isDirty = useIsDirty(translations, loaded);

    const { textId } = useParams<{ textId: string }>();
    const textEntityId = Number(textId);
    const { action, icon } = EntityColors.text;

    const siteLanguages = useSiteLanguages(currentSite.id);
    const enabledCodes = siteLanguages?.map(l => l.languageCode) ?? [];

    useEffect(() => {
        const load = async () => {
            const resp = await getText(Number(textId));
            if (resp) {
                const sorted = [...resp.translations].sort((a, b) =>
                    a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1
                );
                setTranslations(sorted);
                setPageId(resp.pageId);
            }
            setLoaded(true);
        };
        void load();
    }, [textId]);

    const doSave = async () => {
        await updateText({ TextId: textEntityId, Translations: translations }, pageId);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        void doSave();
    };

    const handleLanguageSelect = (short: LanguageCode) => {
        if (!translations.some(t => t.languageShortcut === short)) {
            setTranslations(prev => [
                ...prev,
                { value: '', languageShortcut: short, textId: textEntityId, isDefault: false },
            ]);
        }
        setShowAddLanguageModal(false);
    };

    const handleDefaultLanguageChange = (short: LanguageCode) => {
        const existing = translations.filter(t => t.languageShortcut !== short);
        const picked = translations.find(t => t.languageShortcut === short) ?? {
            value: '', languageShortcut: short, textId: textEntityId, isDefault: false,
        } as Translation;
        setTranslations([{ ...picked, isDefault: true }, ...existing.map(t => ({ ...t, isDefault: false }))]);
        setShowChangeLanguageModal(false);
    };

    const handleTextChange = (langShortcut: LanguageCode, val: string) => {
        setTranslations(prev => prev.map(t => (t.languageShortcut === langShortcut ? { ...t, value: val } : t)));
    };

    const handleRemoveLanguage = (langShortcut: LanguageCode) => {
        setTranslations(prev => prev.filter(t => t.languageShortcut !== langShortcut));
    };

    // New translations only in enabled languages; existing ones in a since-removed language stay editable.
    const addOptions = enabledCodes
        .filter(code => !translations.some(t => t.languageShortcut === code));

    const changeDefaultOptions = Array.from(new Set([...enabledCodes, ...translations.map(t => t.languageShortcut)]))
        .filter(code => !translations.some(t => t.isDefault && t.languageShortcut === code));

    const tableHref = pageId != null ? `/text/table/${pageId}` : '/text/table';

    return (
        <>
            <FormShell title="Úprava textu" icon={icon} accentColor={action} backHref={tableHref} isDirty={isDirty}>
                {!loaded ? (
                    <p className="text-center py-4 text-secondary">Načítavam...</p>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="st-trans-stack">
                            {translations.length === 0 && (
                                <p className="st-trans-empty">
                                    Tento text zatiaľ nemá žiadne preklady. Pridajte prvý jazyk.
                                </p>
                            )}

                            {translations.map(tr => (
                                <div
                                    key={tr.languageShortcut}
                                    className={`st-trans-card${tr.isDefault ? ' st-trans-card--default' : ''}`}
                                    style={{ '--trans-accent': action } as React.CSSProperties}
                                >
                                    <div className="st-trans-card__head">
                                        <div className="st-trans-card__id">
                                            <FlagImage code={tr.languageShortcut} size="sm" />
                                            <span className="st-trans-card__lang">
                                                {languageName(tr.languageShortcut)}
                                            </span>
                                            {tr.isDefault && (
                                                <span className="st-trans-card__badge">
                                                    Predvolený preklad
                                                </span>
                                            )}
                                            <TranslationLanguageBadges code={tr.languageShortcut} siteLanguages={siteLanguages} />
                                        </div>
                                        {tr.isDefault ? (
                                            <EditButton color="gray" text="Zmeniť" onClick={() => setShowChangeLanguageModal(true)} />
                                        ) : (
                                            <DeleteButton iconOnly onClick={() => handleRemoveLanguage(tr.languageShortcut)} />
                                        )}
                                    </div>
                                    <div className="text-edit-quill st-trans-card__body">
                                        <ReactQuill
                                            modules={quillModules}
                                            value={tr.value}
                                            onChange={text => handleTextChange(tr.languageShortcut, text)}
                                        />
                                    </div>
                                </div>
                            ))}

                            <AddLanguageTile accentColor={action} onClick={() => setShowAddLanguageModal(true)} />
                        </div>

                        <div className="d-flex justify-content-center gap-3 mt-4">
                            <DiscardButton to={tableHref} isDirty={isDirty} />
                            <SaveButton type="submit" onSave={doSave} websiteId={currentSite.id} />
                        </div>
                    </form>
                )}
            </FormShell>

            <LanguagePickerModal
                show={showAddLanguageModal}
                title="Vyberte jazyk"
                accentColor={action}
                options={addOptions}
                onHide={() => setShowAddLanguageModal(false)}
                onPick={handleLanguageSelect}
                emptyContent={<EnableLanguagesHint />}
            />

            <LanguagePickerModal
                show={showChangeLanguageModal}
                title="Vyberte predvolený jazyk"
                accentColor={action}
                options={changeDefaultOptions}
                onHide={() => setShowChangeLanguageModal(false)}
                onPick={handleDefaultLanguageChange}
            />
        </>
    );
};

export default TextEditComponent;
